import { type Query, request } from "../api/client.ts";
import type { ItemDetail, SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import type { DropTarget } from "./fileDrop.ts";
import { spaceApi, spaceUrl } from "./paths.ts";
import { tabHref } from "./router.ts";
import { titleOf } from "./titles.ts";
import { toast } from "./toast.ts";

/** The relationship files are attached under unless a ref field is named. */
export const ATTACHMENTS = "attachments";

/** When a file was last modified on the uploader's computer, as the browser knows it (kept with the file). */
const modifiedOf = (f: File): string | null => (f.lastModified > 0 ? new Date(f.lastModified).toISOString() : null);

/**
 * Uploads files, one at a time (memory and bandwidth stay predictable), each becoming a file item; `query` says where
 * it goes besides (onto an item, private to you…).
 */
async function uploadFiles(space: SpaceOut, files: readonly File[], query: Query = {}): Promise<void> {
  for (const f of files) {
    // biome-ignore lint/performance/noAwaitInLoops: one upload at a time keeps memory and bandwidth predictable.
    await request<ItemDetail>("POST", spaceApi(space, "/files"), {
      query: { name: f.name, ...query, modified: modifiedOf(f) },
      raw: { data: f, type: f.type },
    });
  }
}

/**
 * Uploads files straight onto an item: each becomes a file item linked from `relation`, in one change on the
 * server (so it never passes through the triage area).
 */
export function attachFiles(space: SpaceOut, item: string, files: readonly File[], relation = ATTACHMENTS) {
  return uploadFiles(space, files, { attach_to: item, relation: relation === ATTACHMENTS ? null : relation });
}

/**
 * Files into the triage area: yours (private to you) or the organization's (`mine` false), then `done`. With `link` (dropped
 * on another page), the toast leads there.
 */
export function triageTarget(
  space: SpaceOut,
  { mine, level, link = false, done }: { mine: boolean; level: number; link?: boolean; done?: () => void },
): DropTarget {
  const tab = mine ? "mine" : "shared";
  return {
    key: `triage:${space.id}:${tab}`,
    label: t(mine ? "drop.toMyTriage" : "drop.toSharedTriage"),
    busy: t("triage.uploading"),
    level,
    take: async (files) => {
      await uploadFiles(space, files, { private: mine || null });
      const there = { label: t("drop.openTriage"), href: tabHref(spaceUrl(space, "/triage"), tab) };
      toast(t("triage.uploaded", { count: files.length }), "info", undefined, link ? [there] : undefined);
      done?.();
    },
  };
}

/** Files attached to `item` (under `attachments`), then `done`. */
export function attachTarget(space: SpaceOut, item: ItemDetail, done: () => void): DropTarget {
  return {
    key: `attach:${item.id}`,
    label: t("drop.attachTo", { title: titleOf(item) }),
    busy: t("attachments.uploading"),
    level: 1,
    take: async (files) => {
      await attachFiles(space, item.id, files);
      toast(t("attachments.added", { count: files.length }));
      done();
    },
  };
}
